<template>
  <tile class="opc-zmaudio">
    <zm-alert v-if="notification" type="warning" show-icon :closable="false" class="opc-base-alert">
      <p class="opc-base-alert__message" v-html="notification"  @mouseover="() => $tracking.buyInteraction('banner - product level', trackingPlanName + ' - ' + notification + ' - impression')" />
    </zm-alert>
    <foldable v-model="foldableExpanded" :title="trackingPlanName" :arrow="!readonly" :edit="!readonly" :isRemoveWrapAriaLabelledby="true">
      <div slot="head" class="opc-zm-base-card__head">
        <div class="opc-zm-base-card__head-left">
          <img class="opc-zm-base-card__logo" :src="logo" alt="Zoom Audio" tabindex="-1" />
        </div>
        <div class="opc-zm-base-card__head-right">
          <h1 class="opc-zm-base-card__name text-capitalize">
            {{ title }}
            <i class="zm-icon-success main-icon" />
          </h1>
          <div class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset" v-if="!foldableExpanded">
            <p v-for="(info, idx) in getAudioBillInfo" :key="idx" class="opc-zm-base-card__bill-info-item">{{ info }}</p>
          </div>
        </div>
      </div>
      <div class="opc-zmaudio__main">
        <p class="opc-zmaudio__desc" v-if="isSDKAudio">{{ $t('billing.tollfree.desc_for_sdk') }}</p>
        <p class="opc-zmaudio__desc" v-else>{{ $t('billing.tollfree.desc') }}</p>
        <!--form-->
        <div class="opc-zmaudio__forms">
          <component
            v-for="form in currentForms"
            :key="form.category"
            :title="form.title"
            :desc="form.desc"
            :is="form.Form"
            @remove="handleRemovePlan"
          />
          <div class="opc-zmaudio__form-add-btn-row" v-if="nextPlan">
            <zm-button type="link" @click="handleAddPlan(nextPlan)">{{ nextPlan.addBtn }}</zm-button>
          </div>
        </div>
        <div class="opc-zm-base-card__main__footer" v-if="couldBeRemovedAsAddonCard">
          <zm-button type="link" ghost @click="removePlanByKey('TOLLFREE')"
            :aria-label="'remove Audio Conferencing plan'">{{ $t('billing.common.remove')
            }}</zm-button>
        </div>
      </div>
    </foldable>
  </tile>
</template>
<script>
import tile from '@/components/tile.vue';
import foldable from '@/components/foldable.vue';
import AudioConferencingIcon from '@/assets/plans/audio_conferencing.svg';
import { AUDIO_SUB_TYPES, planTypes, BILLING_CYCLE } from '@/common/constants';
import AudioInternational from './forms/audio-international.vue';
import AudioUsCa from './forms/audio-us-ca.vue';
import _ from 'lodash';
import computedMixins from '@/plans/TOLLFREE/mixins/computed-mixins';
import plansMixin from '@/plans/mx/plan';
import { mapGetters } from 'vuex';
import { ONLINE_NOT_SUPPORT_BILLING_CYCLE } from "@/common/service/non-standard-billing-cycle-utils";

const categoryNameMap = {
  [AUDIO_SUB_TYPES.TOLL_FREE]: 'billing.tollfree.audio_conferencing.title',
  [AUDIO_SUB_TYPES.UNLIMITED]: 'billing.tollfree.audio_us_ca.title'
};

export default {
  name: 'AudioConfig',
  mixins: [computedMixins, plansMixin],
  components: {
    tile,
    foldable,
  },
  data() {
    return {
      readonly: false,
      isExpanded: true,
      title: this.$t('billing.tollfree.audio_conferencing.title'),
      trackingPlanName: this.$t('billing.tollfree.audio_conferencing.title'),
      logo: AudioConferencingIcon,
      notification: '',
    }
  },
  computed: {
    ...mapGetters(['getCurrentBasePlanInfo']),
    foldableExpanded: {
      get() {
        return this.readonly ? false : this.isExpanded;
      },
      set(val) {
        this.isExpanded = val;
      },
    },
    selectedPlans() {
      return this.planFilter(this.getSelectedPlans);
    },
    selectedPlanCategories() {
      return this.selectedPlans.map(item => item.category);
    },
    audioPlans() {
      return this.planFilter(this.planMapFlat);
    },
    audioPlansGroupByCategory() {
      return _.groupBy(this.audioPlans, 'category');
    },
    allAudioCategories() {
      return Object.keys(this.audioPlansGroupByCategory);
    },
    isSDKAudio() {
      return this.getCurrentBasePlanInfo.planType === planTypes.VIDEO_SDK;
    },
    audioForms() {
      let rateLink = "/zoomconference/rates";
      if(this.isSDKAudio) {
        rateLink = "/zoomconference/rates?plan=video_sdk";
      }
      if(!this.mx_showUnlimited) {
        return [
          {
            title: this.$t('billing.tollfree.audio_conferencing_plan'),
            desc: this.$t('billing.tollfree.audio_conferencing.desc', [rateLink]),
            Form: AudioInternational,
            addBtn: null,
            category: AUDIO_SUB_TYPES.TOLL_FREE,
          }
        ]
      } else {
        return [
          {
            title: this.$t('billing.tollfree.audio_us_ca.title'),
            desc: this.$t('billing.tollfree.audio_us_ca.desc'),
            addBtn: this.$t('billing.tollfree.add_us_ca_audio'),
            Form: AudioUsCa,
            category: AUDIO_SUB_TYPES.UNLIMITED,
          },
          {
            title: this.mx_hasUnlimitedPlan ? this.$t('billing.tollfree.need_beyound_us_ca') : this.$t('billing.tollfree.audio_conferencing_plan'),
            desc: this.$t('billing.tollfree.view_rates_link', [rateLink]),
            addBtn: this.$t('billing.tollfree.add_additional_countries_regions'),
            Form: AudioInternational,
            category: AUDIO_SUB_TYPES.TOLL_FREE,
          }
        ]
      }
    },
    currentForms() {
      return this.audioForms.filter(item => this.selectedPlanCategories.includes(item.category));
    },
    restForms() {
      return this.audioForms.filter(item => !this.selectedPlanCategories.includes(item.category));
    },
    nextPlan() {
      return this.restForms[0];
    },
    couldBeRemovedAsAddonCard() {
      return this.$context.type !== planTypes.TOLLFREE && !this.readonly;
    },
    getAudioBillInfo() {
      const needShowDetailName = this.selectedPlanCategories.length > 1;
      const billingInfoGetter = {
        [AUDIO_SUB_TYPES.TOLL_FREE]: (plan) => {
          const planName =  needShowDetailName ? `${this.$t('billing.tollfree.audio_conferencing.title')} • ` : '';
          if(plan.billing_cycle === BILLING_CYCLE.PAY_AS_YOU_GO) {
            return planName + this.$t('billing.pay_you_go');
          } else if(plan.billing_cycle === BILLING_CYCLE.MONTHLY) {
            return planName + `${this.$money(plan.amount)}${this.$t('billing.common.per_billing_cycle.monthly')}`
          } else if(plan.billing_cycle === BILLING_CYCLE.QUARTERLY) {
            return planName + `${this.$money(plan.amount)}${this.$t('billing.common.per_billing_cycle.quarterly')}`
          } else if(plan.billing_cycle === BILLING_CYCLE.ANNUAL) {
            return planName + `${this.$money(plan.amount)}${this.$t('billing.common.per_billing_cycle.annual')}`
          } else if(plan.billing_cycle === BILLING_CYCLE.TWO_YEARS) {
            return planName + `${this.$money(plan.amount)}${this.$t('billing.common.per_billing_cycle.two_years')}`
          } else if(plan.billing_cycle === BILLING_CYCLE.THREE_YEARS) {
            return planName + `${this.$money(plan.amount)}${this.$t('billing.common.per_billing_cycle.three_years')}`
          }
          return '';
        },
        [AUDIO_SUB_TYPES.UNLIMITED]: (plan) => {
          const planName =  needShowDetailName ? `${this.$t('billing.tollfree.audio_us_ca.title')} • ` : '';
          if(plan.billing_cycle === BILLING_CYCLE.ANNUAL) {
            return planName + `${plan.cartInfo.selected_number} ${this.$t('billing.licenses')} • ${this.$money(plan.amount)}${this.$t('billing.common.per_billing_cycle_license.annual')}`;
          } else if(plan.billing_cycle === BILLING_CYCLE.MONTHLY) {
            return planName + `${plan.cartInfo.selected_number} ${this.$t('billing.licenses')} • ${this.$money(plan.amount)}${this.$t('billing.common.per_billing_cycle_license.monthly')}`;
          } else if(plan.billing_cycle === BILLING_CYCLE.QUARTERLY) {
            return planName + `${plan.cartInfo.selected_number} ${this.$t('billing.licenses')} • ${this.$money(plan.amount)}${this.$t('billing.common.per_billing_cycle_license.quarterly')}`;
          } else if(plan.billing_cycle === BILLING_CYCLE.TWO_YEARS) {
            return planName + `${plan.cartInfo.selected_number} ${this.$t('billing.licenses')} • ${this.$money(plan.amount)}${this.$t('billing.common.per_billing_cycle_license.two_years')}`;
          } else if(plan.billing_cycle === BILLING_CYCLE.THREE_YEARS) {
            return planName + `${plan.cartInfo.selected_number} ${this.$t('billing.licenses')} • ${this.$money(plan.amount)}${this.$t('billing.common.per_billing_cycle_license.three_years')}`;
          }
          return '';
        }
      }
      return this.mx_getSelectedAudios.map(item => billingInfoGetter[item.category](item)).filter(Boolean);
    },
    firstSelectCategory() {
      return this.selectedPlanCategories[0];
    },
  },
  created() {
    this.initAudioForm();
  },
  methods: {
    initAudioForm() {
      if(this.mx_getSelectedAudios.length) return;
      // For NN or F2P, must have a audio plan selected;
      if(this.mx_showUnlimited) {
        const monthlyUnlimitedPlan = this.mx_getAudioPlans
        .find(item => item.category === AUDIO_SUB_TYPES.UNLIMITED && item.billing_cycle === BILLING_CYCLE.MONTHLY);
        monthlyUnlimitedPlan && this.updateSelectPlan({
          plan_code: monthlyUnlimitedPlan.plan_code,
          cartInfo: {
            selected_number: this.unlimitedAudioLicense,
          },
        });
      } else {
        this.mx_monthlyAudio.plan_code && this.mx_addAudioPlan(this.mx_monthlyAudio.plan_code, this.mx_monthlyAudio.billing_cycle);
      }
    },
    planFilter(plans) {
      return plans.filter(item => item.type === planTypes.TOLLFREE);
    },
    getFormsMap(plans) {
      const map = {};
      plans.forEach(item => {
        map[item.category] = map[item.category] || [];
        map[item.category].push(item);
      });
      return map;
    },
    getValue(category) {
      return this.selectedPlans.find(item => item.category === category);
    },
    handleRemovePlan(category) {
      const categoryPlans = this.mx_getSelectedAudios.filter(item => item.category === category);
      categoryPlans.forEach(item => this.removePlanAction(item.plan_code));

      this.$tracking.buyInteraction(
        `Audio Conferencing options - ${this.$t(categoryNameMap[category])}`,
        'remove',
      );
    },
    handleAddPlan(plan) {
      const { category } = plan;
      const targetPlans = this.audioPlans.filter(item => item.category === category);
      let defaultCycle = '', defaultLicense = 1;
      if(category === AUDIO_SUB_TYPES.TOLL_FREE) {
        defaultCycle = BILLING_CYCLE.MONTHLY;
      } else if(category === AUDIO_SUB_TYPES.UNLIMITED) {
        defaultCycle = this.mx_basePlanBillingCycle;
        if (ONLINE_NOT_SUPPORT_BILLING_CYCLE.includes(defaultCycle)) {
          defaultCycle = BILLING_CYCLE.MONTHLY;
        }
        defaultLicense = this.unlimitedAudioLicense
      }
      const defaultPlan = targetPlans.find(item => item.billing_cycle === defaultCycle) || targetPlans[0];
      defaultPlan && this.updateSelectPlan({
        ...defaultPlan,
        planType: planTypes.TOLLFREE,
        cartInfo: { selected_number: defaultLicense },
      });
      this.$tracking.buyInteraction(
        `Audio Conferencing options - ${this.$t(categoryNameMap[category])}`,
        plan.addBtn,
      );
    },
  },
  watch: {
    isExpanded: {
      handler(val) {
        this.$tracking.addOnBuyInteraction(
          `${this.trackingPlanName} accordion`,
          `${val ? 'edit' : 'collapse'}`,
        );
      },
    },
  },
}
</script>
<style lang="scss">
.opc-zmaudio {
  &__main {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #dfdfe4;
  }
  &__desc {
    color: var(--neutral-neutral-08, #515079);
    /* body/LF — S (R) */
    font-family: Almaden Sans;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%; /* 21px */
  }
  &__form-add-btn-row {
    border-top: 1px solid #DFE3E8;
    margin-top: 24px;
    padding: 24px 0 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__form-item {
    padding-top: 24px;
    margin-bottom: 24px;
    &:last-of-type {
      border-top: 1px solid #DFE3E8;
      margin-bottom: 0;
    }
    &:first-of-type {
      border-top: none;
    }
  }
}
.opc-zm-base-card {
  &-item {
    color: var(--day-foreground-foreground-strong, #00031F);

    /* label/S (M) */
    font-family: Almaden Sans;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 120%; /* 16.8px */
  }
}
.opc-base-alert {
  &.zm-alert {
    background: #F7F7F8;
    margin-bottom: 24px !important;

    .zm-alert__icon {
      color: #666487 !important;
    }
  }

  &__message {
    font-family: Almaden Sans;
    font-size: 14px;
    font-weight: 450;
    line-height: 21px;
    letter-spacing: 0em;
    text-align: left;
  }
}
</style>
